import './migration-strategy.scss';

import { Alert, Box, Container, ExpandableSection, Grid, Input, NonCancelableCustomEvent, Popover, SpaceBetween } from '@amzn/awsui-components-react';
import { BaseChangeDetail } from '@amzn/awsui-components-react/polaris/input/interfaces';
import { useContext, useEffect, useState } from 'react';

import { constants } from '../../common/Localization';
import { MONTHS_PER_YEAR, ONE_HUNDRED, ONE_THOUSAND, QUARTERS_PER_YEAR, WEEKS_PER_YEAR, WEEKS_TO_MONTH } from '../../common/uomConversion';
import { deepCopy, roundToHundredth } from '../../common/util';
import { TcoContext } from '../../contexts/TcoContext';
import { DistributionTime } from './interfaces';

interface Column {
    value: string;
    text: string;
    template?: string;
}

interface Row {
    tranche: number;
    distributionByYear: string[];
    disabled: boolean[];
}

interface Props {
    migrationTranche: DistributionTime;
}

function MigrationCashflowCompletionPlan( { migrationTranche }: Props) {

    const [ columns, setColumns ] = useState<Column[]>([]);
    const [ rows, setRows ] = useState<Row[]>([]);
    const [ percentageSum, setPercentageSum ] = useState<number>(0);
    const [ isValid, setIsValid ] = useState(true);
    const { assessmentToUpdate, updateAssessmentToUpdate } = useContext(TcoContext);
    const { projectDurationWeeks } = assessmentToUpdate?.configuration.migrationToolsAndSchedule || { projectDurationWeeks: 2 * WEEKS_PER_YEAR };
    const { migrationCompletionDistribution } = assessmentToUpdate?.input.migration || {};

    useEffect(() => {
        createColumns();
        const migrationDistribution = migrationCompletionDistribution ?? 
            createUniformMigrationCostDistribution(
                Number(projectDurationWeeks), migrationTranche.unitInMonths);
        createRows(migrationDistribution, Number(projectDurationWeeks), migrationTranche.unitInMonths);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [migrationTranche]);

    const createColumns = (): void => {
        const years = Math.ceil(Number(projectDurationWeeks) / WEEKS_PER_YEAR);
        const columns: Column[] = [{
            value: 'timePeriod',
            text: '',
        }];
        for (let index=0; index<years; index++) {
            columns.push({
                value: index.toString(),
                text: `${constants.YEAR} ${index + 1}`,
                template: 'yearTemplate'
            });
        }
        setColumns(columns);
    };

    const createRows = (distributionByTranche: number[], durationWeeks: number, migrationTranche: number) => {
        const formattedDistribution: Row[] = [];
        const tranchesPerYear = MONTHS_PER_YEAR / migrationTranche ?? QUARTERS_PER_YEAR;
        const numberOfYears = Math.ceil(durationWeeks / WEEKS_PER_YEAR);
        const totalTranches = (Number(projectDurationWeeks) / WEEKS_PER_YEAR) * tranchesPerYear;

        for (let tranche = 1; tranche <= tranchesPerYear * numberOfYears; ++tranche) {
            const distribution = (tranche <= totalTranches && distributionByTranche.length >= tranche) ? distributionByTranche[tranche-1] : 0;

            if (tranche <= tranchesPerYear) {
                formattedDistribution.push({
                    tranche: tranche,
                    distributionByYear: [distribution.toString()],
                    disabled: [ tranche > totalTranches]
                });
            } else {
                formattedDistribution[((tranche - 1) % tranchesPerYear)].distributionByYear.push(distribution.toString());
                formattedDistribution[((tranche - 1) % tranchesPerYear)].disabled.push(tranche > totalTranches);
            }
        }
        setRows(formattedDistribution);
    };

    const createUniformMigrationCostDistribution = (durationWeeks: number, migrationTranche: number): number[] => {
        const duration = Math.ceil(durationWeeks / WEEKS_TO_MONTH / migrationTranche);
        const migrationDistribution = [];
        let totalDistribution = 0;
        const distributionPerTranche = ONE_HUNDRED / duration;
        const durationDistribution = roundToHundredth(distributionPerTranche);

        for (let index=1; index<duration; index++) {
            migrationDistribution.push(durationDistribution);
            totalDistribution += durationDistribution;
        }

        migrationDistribution.push(roundToHundredth(ONE_HUNDRED - totalDistribution));
        return migrationDistribution;
    };

    const isInvalidInput = (input: string): boolean => {
        input = input ?? 0;
        const numberInput = Number(input);
        return (numberInput < 0) || !((numberInput.toFixed(2) === input) || (numberInput.toFixed(1) === input) || (numberInput.toFixed() === input));
    };

    const onValueChanged = (rowIndex: number, colIndex: number) => (event: NonCancelableCustomEvent<BaseChangeDetail>) => {
        const newRows: Row[] = deepCopy(rows);
        newRows[rowIndex].distributionByYear[colIndex] = event.detail.value;
        setRows(newRows);

        const costDistribution = newRows.map(row => row.distributionByYear);
        let year = 0;
        const percentageList: number[] = [];
        while (year < costDistribution[0].length) {
            costDistribution.forEach(row => {
                const value = Number(row[year]);
                if (!isNaN(value)) {
                    percentageList.push(value);
                }
            });
            year += 1;
        }

        const percentageSum = percentageList.reduce((value, sum) => {
            return (Number(value) * ONE_THOUSAND + Number(sum) * ONE_THOUSAND) / ONE_THOUSAND;
        });
        setPercentageSum(percentageSum);
        setIsValid(!isInvalidInput(event.detail.value) && percentageSum === ONE_HUNDRED);
        
        if (percentageSum == ONE_HUNDRED && assessmentToUpdate !== undefined)
        {
            const updatedAssessment = Object.assign(assessmentToUpdate, {
                input: {
                    ...assessmentToUpdate.input, 
                    migration: {
                        ...assessmentToUpdate.input.migration, 
                        migrationCompletionDistribution: percentageList
                    }
                }
            });
            updateAssessmentToUpdate(updatedAssessment);
        }
    };

    const gridColDefinition = { colspan: {default: 2, xxs: 4, xs: 2} };

    const getRowClassName = (isFirstRow: boolean) => {
        return isFirstRow ? 'first-distribution-item' : '';
    };

    const getContent = () => {
        const gridDefinition = [gridColDefinition];
        columns.forEach(() => {
            gridDefinition.push(gridColDefinition);
        });

        const array = columns.map((column, colIndex) => {
            const rowArray = rows.map((row, rowIndex) => {
                if (colIndex === 0) {
                    return (
                        <Box className={getRowClassName(rowIndex === 0)} padding={{bottom: 's'}} key={row.tranche}>
                            {migrationTranche.label} {row.tranche}
                        </Box>
                    );
                }
                return (
                    <SpaceBetween size='xxs' direction='horizontal' key={rowIndex + colIndex}>
                        <Input
                            className='completion-plan-input'
                            value={row.distributionByYear[colIndex - 1]}
                            placeholder='0'
                            onChange={onValueChanged(rowIndex, colIndex - 1)}
                            invalid={isInvalidInput(row.distributionByYear[colIndex - 1])}
                        /><div>%</div>
                    </SpaceBetween>
                );
            });
            return (
                <Box padding={'s'} key={colIndex}>
                    <Box padding={{bottom: 'xs'}}>
                        {column.text}
                    </Box>
                    {rowArray}
                </Box>
            );
        });
        return (
            <Grid disableGutters gridDefinition={gridDefinition}>
                {array}
            </Grid>
        );
    };

    return (
        <SpaceBetween size='xxs'>
            <Popover
                content={constants.COMPLETION_PLAN_POPOVER}
            >
                <Box variant='h2'>{constants.MIGRATION_COMPLETION_PLAN}</Box>
            </Popover>
            <Box padding={{left: 'm'}}>
                <ExpandableSection
                    defaultExpanded
                    header={constants.MIGRATION_COMPLETION_PLAN_DESCRIPTION}
                >
                    <Container>
                        <Alert
                            type='error'
                            visible={!isValid}
                            header={constants.INVALID_DISTRIBUTION}
                        >
                            {constants.COMPLETION_PLAN_WARNING} {percentageSum}%.
                        </Alert>
                        {getContent()}
                    </Container>
                </ExpandableSection>
            </Box>
        </SpaceBetween>
    );
}

export default MigrationCashflowCompletionPlan;
